import {
  Box,
  CircularProgress,
  Container,
  Modal,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { Suspense, lazy, useState } from "react";

import { renderErrorOrLoading } from "../commons/renderErrorOrLoading";
import { LoginIndicator } from "../components/LoginIndicator";
import { MatchupToolbar } from "../components/MatchupToolbar";
import { MatchupView } from "../components/MatchupView";
import { Standings } from "../components/Standings";
import { Winners } from "../components/Winners";
import { glassPanelSx } from "../components/glassPanel";
import { appConfig } from "../config";
import { overviewCopy, winnersCopy } from "../config/appCopy";
import { countResolved, isResolved } from "../data/matchupProgress";
import { sortMatchupsByKickoff } from "../data/matchupSorting";
import type { MatchupRow } from "../data/types";
import { useData } from "../data/useData";
import { usePersistentString } from "../data/usePersistentString";
import { useUser } from "../data/useUser";
import { ResultsDialog } from "./ResultsDialog";
import { UserDialog } from "./UserDialog";

const GUEST_USER = "GUEST";
const WINNERS_VIEW = "winners";
const OVERVIEW_VIEW = "overview";
const RESULTS_VIEW = "results";

// Code-split the Overview (and its @mui/x-charts payload) so it only loads when
// a user opens the tab — keeps the initial Results/Winners bundle light on mobile.
const Overview = lazy(() =>
  import("../components/Overview").then((module) => ({
    default: module.Overview,
  })),
);

const tabSx = {
  color: "text.primary",
  fontWeight: 600,
  "&.Mui-selected": { color: "primary.main" },
} as const;

export function Main(): JSX.Element {
  const { matchups = [], rows, loading, error } = useData();
  const { user, setUser } = useUser();
  const [selectedMatchup, setSelectedMatchup] = useState<MatchupRow>();
  const [storedHideResolved, setStoredHideResolved] = usePersistentString(
    appConfig.hideResolvedStorageKey,
    "0",
  );
  const [storedView, setStoredView] = usePersistentString(
    appConfig.activeViewStorageKey,
    RESULTS_VIEW,
  );
  const errorOrLoading = renderErrorOrLoading({ loading, error });

  if (errorOrLoading) return errorOrLoading;

  const view =
    storedView === WINNERS_VIEW
      ? WINNERS_VIEW
      : storedView === OVERVIEW_VIEW
        ? OVERVIEW_VIEW
        : RESULTS_VIEW;
  const hideResolved = storedHideResolved === "1";
  const progress = countResolved(matchups);
  const orderedMatchups = sortMatchupsByKickoff(matchups);
  const visibleMatchups = hideResolved
    ? orderedMatchups.filter((matchup) => !isResolved(matchup))
    : orderedMatchups;
  const firstUnresolved = orderedMatchups.find(
    (matchup) => !isResolved(matchup),
  );
  // Mirror the card anchor id (`matchup-${matchup.index ?? listIndex}`); only offer
  // the link when we can build a matching target id, so it can never silently no-op.
  const latestMatchupId =
    firstUnresolved?.index !== undefined
      ? `matchup-${firstUnresolved.index}`
      : undefined;
  const goToLatest = latestMatchupId
    ? () => scrollToMatchup(latestMatchupId)
    : undefined;

  return (
    <Container component="main" maxWidth="lg" sx={{ py: { xs: 3, sm: 4 } }}>
      <LoginIndicator />
      <Stack spacing={{ xs: 3, sm: 4 }}>
        <Box>
          <Typography variant="h4" component="h1">
            {appConfig.tournamentName} {viewLabel(view)}
          </Typography>
        </Box>
        <Box sx={{ ...glassPanelSx, alignSelf: "flex-start", px: 1, py: 0.5 }}>
          <Tabs
            value={view}
            onChange={(_event, value: string) => setStoredView(value)}
            aria-label="views"
            slotProps={{
              indicator: {
                sx: { backgroundColor: "secondary.main", height: 3 },
              },
            }}
          >
            <Tab
              label={winnersCopy.resultsTabLabel}
              value={RESULTS_VIEW}
              id="tab-results"
              aria-controls="panel-results"
              sx={tabSx}
            />
            <Tab
              label={winnersCopy.winnersTabLabel}
              value={WINNERS_VIEW}
              id="tab-winners"
              aria-controls="panel-winners"
              sx={tabSx}
            />
            <Tab
              label={overviewCopy.overviewTabLabel}
              value={OVERVIEW_VIEW}
              id="tab-overview"
              aria-controls="panel-overview"
              sx={tabSx}
            />
          </Tabs>
        </Box>
        {view === WINNERS_VIEW && (
          <Box role="tabpanel" id="panel-winners" aria-labelledby="tab-winners">
            <Winners />
          </Box>
        )}
        {view === OVERVIEW_VIEW && (
          <Box role="tabpanel" id="panel-overview" aria-labelledby="tab-overview">
            <Suspense fallback={<OverviewFallback />}>
              <Overview />
            </Suspense>
          </Box>
        )}
        {view === RESULTS_VIEW && (
          <Box role="tabpanel" id="panel-results" aria-labelledby="tab-results">
            <Stack spacing={{ xs: 3, sm: 4 }}>
              <Typography color="text.secondary">
                Standings and matchup results
              </Typography>
              <Standings />
              <Box>
                <MatchupToolbar
                  resolved={progress.resolved}
                  total={progress.total}
                  hideResolved={hideResolved}
                  onToggleHideResolved={(hide) =>
                    setStoredHideResolved(hide ? "1" : "0")
                  }
                  onGoToLatest={goToLatest}
                />
                <Box
                  aria-label="matchups"
                  component="section"
                  sx={{
                    alignItems: "start",
                    display: "grid",
                    gap: { xs: 2.5, sm: 3 },
                    gridTemplateColumns: {
                      xs: "minmax(0, 1fr)",
                      md: "repeat(2, minmax(0, 1fr))",
                      lg: "repeat(3, minmax(0, 1fr))",
                    },
                    mt: 2,
                    width: "100%",
                  }}
                >
                  {visibleMatchups.map((matchup, index) => (
                    <MatchupView
                      key={matchup.index ?? index}
                      {...matchup}
                      index={matchup.index ?? index}
                      onOpen={() => setSelectedMatchup(matchup)}
                    />
                  ))}
                </Box>
              </Box>
            </Stack>
          </Box>
        )}
      </Stack>
      <Modal open={!user} onClose={() => setUser(GUEST_USER)}>
        <UserDialog rows={rows} onSetUser={setUser} />
      </Modal>
      {selectedMatchup && (
        <Modal open onClose={() => setSelectedMatchup(undefined)}>
          <ResultsDialog
            matchup={selectedMatchup}
            onClose={() => setSelectedMatchup(undefined)}
          />
        </Modal>
      )}
    </Container>
  );
}

function OverviewFallback(): JSX.Element {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
      <CircularProgress />
    </Box>
  );
}

function viewLabel(view: string): string {
  if (view === WINNERS_VIEW) return winnersCopy.winnersTabLabel;
  if (view === OVERVIEW_VIEW) return overviewCopy.overviewTabLabel;
  return winnersCopy.resultsTabLabel;
}

function scrollToMatchup(id: string): void {
  const target = document.getElementById(id);
  if (!target) return;
  target.scrollIntoView({ behavior: "smooth", block: "start" });
  // Move focus too, so keyboard/screen-reader users land on the match (WCAG 2.4.3).
  target.focus({ preventScroll: true });
}
